import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, Lock, Plus, Search, Trash2, XCircle } from 'lucide-react';
import { apiFetch, ApiClientError, getApiFieldError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useSkillLevelSimplified } from '@/hooks/useSkillLevelSimplified';
import { BehaviorDescriptionHint } from '@/components/BehaviorDescriptionHint';
import { deriveSkillLevelFulfillment } from '@/lib/skill-level-comparison';
import { SkillFulfillmentSummary, CriteriaText, FulfillmentIndicator } from '@/components/SkillLevelFulfillment';
import {
  useSkillMatrixRows, type SkillMatrixRow, type DevelopmentPlanApi, type SkillMatrixMasterLevel,
} from '@/hooks/useSkillMatrixRows';

/**
 * スキル登録表 (編集可能、mode:'managed')。EmployeeDetailPage.tsx の「スキル・人材像」タブと
 * Drawer「スキル登録」タブの両方から使う (docs/design/v05-skill-registration-table-redesign-detail.md §3/§4)。
 * 旧 `EmployeeSkillsPanel` を置き換える。
 */
export function SkillMatrixEditor({ employeeId, canManage }: { employeeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const m = useSkillMatrixRows({ mode: 'managed', employeeId });
  const { isSimplified: skillLevelSimplified, defaultLevelId } = useSkillLevelSimplified();
  const [historySkillId, setHistorySkillId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['employee-skills', employeeId] });
    qc.invalidateQueries({ queryKey: ['development-plans-by-employee', employeeId] });
  };

  const roleProfileMut = useMutation({
    mutationFn: (roleProfileIds: string[]) => apiFetch(`/employees/${employeeId}/skills`, {
      method: 'PUT', body: JSON.stringify({ role_profile_ids: roleProfileIds }),
    }),
    onSuccess: () => { invalidateAll(); toast.success('人材像を更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const skillLevelMut = useMutation({
    mutationFn: ({ skillId, skillLevelId }: { skillId: string; skillLevelId: string }) => apiFetch(`/employees/${employeeId}/skills`, {
      method: 'PUT', body: JSON.stringify({ skills: [{ skill_id: skillId, skill_level_id: skillLevelId }] }),
    }),
    onSuccess: () => invalidateAll(),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'スキルの更新に失敗しました'),
  });

  if (m.isLoading) return <div className="text-text-sub">読み込み中…</div>;
  if (m.isError || !m.employeeId) {
    return <p className="text-sm text-text-sub">スキル・人材像情報を閲覧する権限がありません。</p>;
  }

  const trueEmpty = m.rows.length === 0;

  return (
    <div className="space-y-6">
      <RoleProfileSection
        roleProfiles={m.roleProfilesMaster} assignedIds={m.assignedRoleProfileIds}
        canManage={canManage} pending={roleProfileMut.isPending}
        onToggle={(next) => roleProfileMut.mutate(next)}
      />

      {!m.requiredLevelAvailable && (
        <p className="rounded border border-surface-line bg-surface-sunken px-3 py-2 text-xs text-text-sub flex items-start gap-1.5">
          <Info size={14} className="shrink-0 mt-0.5" aria-hidden />
          {m.requiredLevelFeatureDisabled ? 'この機能は現在無効化されています' : 'お使いの権限では人材像が求める水準は表示されません'}
        </p>
      )}

      {m.requiredLevelAvailable && !trueEmpty && (
        <SkillFulfillmentSummary rows={m.rows} skillLevelsMaster={m.skillLevelsMaster} />
      )}

      {trueEmpty ? (
        <EmptyState
          message="まだ登録されているスキルがありません"
          description="人材像を選択するか、下のボタンから個別にスキルを追加できます"
          actionLabel="+ スキルを追加"
          onAction={() => setAddOpen(true)}
        />
      ) : (
        <>
          <SkillMatrixDesktopTable
            m={m} skillLevelSimplified={skillLevelSimplified} canManage={canManage}
            onSkillLevelChange={(skillId, levelId) => skillLevelMut.mutate({ skillId, skillLevelId: levelId })}
            onOpenHistory={setHistorySkillId}
          />
          <SkillMatrixMobileCards
            m={m} skillLevelSimplified={skillLevelSimplified} canManage={canManage}
            onSkillLevelChange={(skillId, levelId) => skillLevelMut.mutate({ skillId, skillLevelId: levelId })}
            onOpenHistory={setHistorySkillId}
          />
        </>
      )}

      {canManage && !trueEmpty && (
        <div>
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setAddOpen(true)}>
            スキルを追加
          </Button>
        </div>
      )}

      <AddSkillPopover
        open={addOpen} onClose={() => setAddOpen(false)} skillsMaster={m.skillsMaster} rows={m.rows}
        skillLevelSimplified={skillLevelSimplified} defaultLevelId={defaultLevelId} skillLevelsMaster={m.skillLevelsMaster}
        onAdd={(skillId, levelId) => { skillLevelMut.mutate({ skillId, skillLevelId: levelId }); setAddOpen(false); }}
      />

      {historySkillId && (
        <SkillPlanHistoryModal
          employeeId={employeeId} skillId={historySkillId}
          skillLabel={m.rows.find((r) => r.skillId === historySkillId)?.skillLabel ?? historySkillId}
          plans={m.rows.find((r) => r.skillId === historySkillId)?.allPlansForSkill ?? []}
          skillLevelsMaster={m.skillLevelsMaster}
          canManage={canManage} onClose={() => setHistorySkillId(null)} onChanged={invalidateAll}
        />
      )}
    </div>
  );
}

function RoleProfileSection({
  roleProfiles, assignedIds, canManage, pending, onToggle,
}: {
  roleProfiles: { role_profile_id: string; label: string }[]; assignedIds: string[];
  canManage: boolean; pending: boolean; onToggle: (next: string[]) => void;
}) {
  return (
    <div>
      <h3 className="text-sm font-bold text-text-body mb-1">人材像</h3>
      <p className="text-xs text-text-sub mb-2">
        選択すると、その人材像に必要なスキルが自動的に展開されます (既存の高い水準はダウングレードされません)。
        複数選択した場合、必要スキルは選択中の全人材像の合算 (最も高い水準) になります。
      </p>
      <div className="border border-surface-border rounded p-2 space-y-1">
        {roleProfiles.map((rp) => (
          <label key={rp.role_profile_id} className="flex items-center gap-2 text-sm min-h-[44px]">
            <input type="checkbox" disabled={!canManage || pending} checked={assignedIds.includes(rp.role_profile_id)}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...assignedIds, rp.role_profile_id]
                  : assignedIds.filter((id) => id !== rp.role_profile_id);
                onToggle(next);
              }}
            />
            {rp.label}
          </label>
        ))}
        {roleProfiles.length === 0 && <p className="text-sm text-text-sub p-1">人材像がまだ登録されていません。</p>}
      </div>
    </div>
  );
}

/** ≥768px: 表形式 (§4-1)。375px以下はカード形式 (SkillMatrixMobileCards、§4-2)。 */
function SkillMatrixDesktopTable({
  m, skillLevelSimplified, canManage, onSkillLevelChange, onOpenHistory,
}: {
  m: ReturnType<typeof useSkillMatrixRows>; skillLevelSimplified: boolean; canManage: boolean;
  onSkillLevelChange: (skillId: string, levelId: string) => void; onOpenHistory: (skillId: string) => void;
}) {
  return (
    <div className="hidden md:block border border-surface-border rounded-lg overflow-hidden bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken text-text-sub">
            <tr>
              <th className="text-left px-3 py-2" scope="col">スキル</th>
              {m.requiredLevelAvailable && <th className="text-left px-3 py-2" scope="col">人材像が求める水準</th>}
              {!skillLevelSimplified && <th className="text-left px-3 py-2" scope="col">現状レベル</th>}
              {!m.developmentPlanFeatureDisabled && (
                <>
                  {!skillLevelSimplified && <th className="text-left px-3 py-2" scope="col">目標レベル</th>}
                  <th className="text-left px-3 py-2" scope="col">目標達成時期</th>
                  <th className="text-left px-3 py-2" scope="col">詳細</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {m.rows.map((row) => (
              <tr key={row.skillId}>
                <td className="px-3 py-2 align-top max-w-[220px]">
                  <span className="inline-flex items-start gap-1 flex-wrap break-words">
                    {row.skillLabel}
                    {row.currentBehaviorDescription && <BehaviorDescriptionHint text={row.currentBehaviorDescription} />}
                  </span>
                </td>
                {m.requiredLevelAvailable && (
                  <td className="px-3 py-2 align-top"><RequiredBadge row={row} skillLevelsMaster={m.skillLevelsMaster} /></td>
                )}
                {!skillLevelSimplified && (
                  <td className="px-3 py-2 align-top">
                    <CurrentLevelSelect row={row} skillLevelsMaster={m.skillLevelsMaster} canManage={canManage && m.canManageSkillLevel}
                      onChange={(levelId) => onSkillLevelChange(row.skillId, levelId)} />
                  </td>
                )}
                {!m.developmentPlanFeatureDisabled && (
                  <PlanCells row={row} employeeId={m.employeeId!} skillLevelSimplified={skillLevelSimplified}
                    skillLevelsMaster={m.skillLevelsMaster} canManage={m.canManageDevelopmentPlan}
                    resolving={m.developmentPlanPermissionResolving} onOpenHistory={onOpenHistory} variant="select" />
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** <768px: 1スキル=1カードの縦積みレイアウト (§4-2)。既存 RosterTab.tsx のパターンを踏襲。 */
function SkillMatrixMobileCards({
  m, skillLevelSimplified, canManage, onSkillLevelChange, onOpenHistory,
}: {
  m: ReturnType<typeof useSkillMatrixRows>; skillLevelSimplified: boolean; canManage: boolean;
  onSkillLevelChange: (skillId: string, levelId: string) => void; onOpenHistory: (skillId: string) => void;
}) {
  return (
    <div className="md:hidden space-y-2">
      {m.rows.map((row) => (
        <div key={row.skillId} className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
          <div className="text-sm font-semibold text-text-body flex items-start gap-1 flex-wrap break-words">
            {row.skillLabel}
            {row.currentBehaviorDescription && <BehaviorDescriptionHint text={row.currentBehaviorDescription} />}
          </div>
          {m.requiredLevelAvailable && (
            <div>
              <span className="text-xs text-text-sub">求める水準</span>
              <RequiredBadge row={row} skillLevelsMaster={m.skillLevelsMaster} />
            </div>
          )}
          {!skillLevelSimplified && (
            <div>
              <span className="text-xs text-text-sub">現状レベル</span>
              <CurrentLevelSelect row={row} skillLevelsMaster={m.skillLevelsMaster} canManage={canManage && m.canManageSkillLevel}
                onChange={(levelId) => onSkillLevelChange(row.skillId, levelId)} />
            </div>
          )}
          {!m.developmentPlanFeatureDisabled && (
            <PlanCells row={row} employeeId={m.employeeId!} skillLevelSimplified={skillLevelSimplified}
              skillLevelsMaster={m.skillLevelsMaster} canManage={m.canManageDevelopmentPlan}
              resolving={m.developmentPlanPermissionResolving} onOpenHistory={onOpenHistory} variant="card" />
          )}
        </div>
      ))}
    </div>
  );
}

function RequiredBadge({ row, skillLevelsMaster }: { row: SkillMatrixRow; skillLevelsMaster: SkillMatrixMasterLevel[] }) {
  if (!row.requiredSkillLevelId) return <span className="text-xs text-text-sub">—</span>;
  const label = skillLevelsMaster.find((l) => l.skill_level_id === row.requiredSkillLevelId)?.label ?? row.requiredSkillLevelId;
  const fulfillment = deriveSkillLevelFulfillment(row.currentSkillLevelId, row.requiredSkillLevelId, skillLevelsMaster);
  return (
    <div>
      <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-surface-sunken border border-surface-border px-2.5 py-1 text-xs text-text-sub">
        <Lock size={10} aria-hidden />必須 {label}
        {row.requiredBehaviorDescription && <BehaviorDescriptionHint text={row.requiredBehaviorDescription} />}
      </span>
      {fulfillment !== 'unknown' && <div className="mt-1"><FulfillmentIndicator fulfilled={fulfillment === 'fulfilled'} /></div>}
      <CriteriaText text={row.levelCriteria[row.requiredSkillLevelId]} />
    </div>
  );
}

function CurrentLevelSelect({
  row, skillLevelsMaster, canManage, onChange,
}: {
  row: SkillMatrixRow; skillLevelsMaster: SkillMatrixMasterLevel[]; canManage: boolean; onChange: (levelId: string) => void;
}) {
  const levels = skillLevelsMaster.filter((l) => l.scale_id === row.scaleId).sort((a, b) => a.sort_order - b.sort_order);
  const criteriaText = row.currentSkillLevelId ? row.levelCriteria[row.currentSkillLevelId] : undefined;
  if (!canManage) {
    const label = skillLevelsMaster.find((l) => l.skill_level_id === row.currentSkillLevelId)?.label;
    return (
      <div>
        <span className="text-sm text-text-body">{label ?? '—'}</span>
        <CriteriaText text={criteriaText} />
      </div>
    );
  }
  return (
    <div>
      <Select value={row.currentSkillLevelId ?? undefined} onValueChange={onChange}>
        <SelectTrigger className="w-full min-w-[120px] min-h-[44px]" aria-label={`${row.skillLabel} 現状レベル`}>
          <SelectValue placeholder="選ぶ" />
        </SelectTrigger>
        <SelectContent>{levels.map((l) => <SelectItem key={l.skill_level_id} value={l.skill_level_id}>{l.label}</SelectItem>)}</SelectContent>
      </Select>
      <CriteriaText text={criteriaText} />
    </div>
  );
}

/** 目標レベル・目標達成時期・詳細 の3列 (table) / 3行 (card) をまとめて描画する。
 *  §4-5: セル単位で即時保存 (blur/onChange 発火)。PUT (row_version 競合) と POST (409重複) で
 *  エラー契約が異なるため判定を分ける。 */
function PlanCells({
  row, employeeId, skillLevelSimplified, skillLevelsMaster, canManage, resolving, onOpenHistory, variant,
}: {
  row: SkillMatrixRow; employeeId: string; skillLevelSimplified: boolean; skillLevelsMaster: SkillMatrixMasterLevel[];
  canManage: boolean; resolving: boolean; onOpenHistory: (skillId: string) => void; variant: 'select' | 'card';
}) {
  const qc = useQueryClient();
  const plan = row.planSelection.kind !== 'none' ? row.planSelection.plan : null;
  const editable = row.planSelection.kind === 'editable';
  const [targetLevelId, setTargetLevelId] = useState(plan?.target_scale_level_id ?? '');
  const [dueDate, setDueDate] = useState(plan?.due_date ?? '');
  const [saving, setSaving] = useState(false);
  // 実装後レビュー MEDIUM是正 (design-and-code、2026-09-20): §4-5は「該当行にインラインメッセージを
  // 表示」と規定していたが、実装当初はグローバルなtoastのみだった。行数の多い表で複数セルを編集中だと
  // 「どの行が競合したか」がtoastだけでは分からないため、行スコープの一時メッセージを追加する。
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  // 認定確定/取消・他行の保存で plan が更新されたら追従する (§8-137型: 常に現在値で上書きしない
  // よう plan_id/row_version が変わったときだけ resync する)
  const planKey = `${plan?.plan_id ?? 'none'}:${plan?.row_version ?? 0}`;
  const lastKeyRef = useRef(planKey);
  // 実装後レビュー HIGH是正: 目標レベル→期日のように同一行の2フィールドを連続保存すると、
  // 1回目のPUT成功後もqueryの再取得(invalidate)が完了するまでplan.row_versionがpropsで
  // 古いまま残り、2回目のPUTが自分自身の直前の更新と楽観ロック競合してしまう(design-expert
  // 実装後レビュー2026-09-20)。row_versionはrefで即時追従させ、再取得を待たない。
  const rowVersionRef = useRef(plan?.row_version);
  useEffect(() => {
    if (lastKeyRef.current !== planKey) {
      lastKeyRef.current = planKey;
      rowVersionRef.current = plan?.row_version;
      setTargetLevelId(plan?.target_scale_level_id ?? '');
      setDueDate(plan?.due_date ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['development-plans-by-employee', employeeId] });
  };

  const save = async (patch: { target_scale_level_id?: string; due_date?: string | null }) => {
    setSaving(true);
    setConflictMessage(null);
    try {
      if (editable && plan) {
        await apiFetch(`/development-plans/${plan.plan_id}`, {
          method: 'PUT', body: JSON.stringify({ ...patch, row_version: rowVersionRef.current }),
        });
        rowVersionRef.current = (rowVersionRef.current ?? plan.row_version) + 1;
      } else {
        const nextTargetLevelId = patch.target_scale_level_id ?? targetLevelId;
        if (!nextTargetLevelId) return;
        await apiFetch('/development-plans', {
          method: 'POST',
          body: JSON.stringify({
            employee_id: employeeId, skill_id: row.skillId, target_scale_level_id: nextTargetLevelId,
            due_date: (patch.due_date ?? dueDate) || null,
          }),
        });
      }
      invalidate();
    } catch (e) {
      if (editable && getApiFieldError(e, 'row_version') !== undefined) {
        setConflictMessage('他の変更と競合しました。最新の内容に更新しました');
        setTimeout(() => setConflictMessage(null), 4000);
        invalidate();
      } else if (e instanceof ApiClientError && e.code === 'DUPLICATE_ACTIVE_PLAN') {
        setConflictMessage('既に別の目標が登録されています。最新の内容に更新しました');
        setTimeout(() => setConflictMessage(null), 4000);
        invalidate();
      } else {
        toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  if (resolving) {
    const skeleton = <span className="inline-block h-[26px] w-24 rounded bg-surface-sunken animate-pulse" aria-hidden />;
    return variant === 'select' ? (
      <>
        {!skillLevelSimplified && <td className="px-3 py-2 align-top">{skeleton}</td>}
        <td className="px-3 py-2 align-top">{skeleton}</td>
        <td className="px-3 py-2 align-top" />
      </>
    ) : (
      <>
        {!skillLevelSimplified && <div><span className="text-xs text-text-sub">目標レベル</span>{skeleton}</div>}
        <div><span className="text-xs text-text-sub">目標達成時期</span>{skeleton}</div>
      </>
    );
  }

  const levels = skillLevelsMaster.filter((l) => l.scale_id === row.scaleId).sort((a, b) => a.sort_order - b.sort_order);
  const resultBadge = row.planSelection.kind === 'readonly_result' && plan ? (
    <span className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${plan.status === 'achieved' ? 'bg-success-bg text-success-text border-success-border' : 'bg-danger-bg text-danger-text border-danger-border'}`}>
      {plan.status === 'achieved' ? '達成' : '未達'}
    </span>
  ) : null;

  const targetCriteriaText = targetLevelId ? row.levelCriteria[targetLevelId] : undefined;
  const targetLevelField = canManage ? (
    <div>
      <Select value={targetLevelId || undefined} disabled={saving}
        onValueChange={(v) => { setTargetLevelId(v); save({ target_scale_level_id: v }); }}>
        <SelectTrigger className="w-full min-w-[120px] min-h-[44px]" aria-label={`${row.skillLabel} 目標レベル`}>
          <SelectValue placeholder="選ぶ" />
        </SelectTrigger>
        <SelectContent>{levels.map((l) => <SelectItem key={l.skill_level_id} value={l.skill_level_id}>{l.label}</SelectItem>)}</SelectContent>
      </Select>
      <CriteriaText text={targetCriteriaText} />
    </div>
  ) : (
    <div>
      <span className="text-sm text-text-body">{skillLevelsMaster.find((l) => l.skill_level_id === targetLevelId)?.label ?? '—'}</span>
      <CriteriaText text={targetCriteriaText} />
    </div>
  );

  const dueDateField = canManage ? (
    <input type="date" value={dueDate} disabled={saving || (!editable && !targetLevelId)}
      onChange={(e) => setDueDate(e.target.value)}
      onBlur={() => save({ due_date: dueDate || null })}
      className="w-full min-h-[44px] px-2 rounded border border-surface-border text-sm disabled:opacity-50" />
  ) : (
    <span className="text-sm text-text-body">{dueDate || '—'}</span>
  );

  const detailLink = row.allPlansForSkill.length > 0 ? (
    <button type="button" onClick={() => onOpenHistory(row.skillId)}
      className="inline-flex items-center gap-1 min-h-[44px] px-1 text-xs text-text-sub underline underline-offset-2 hover:text-text-body">
      <Search size={12} aria-hidden />詳細
    </button>
  ) : null;

  const conflictNote = conflictMessage ? (
    <p role="status" className="text-[11px] text-alert-text mt-1">{conflictMessage}</p>
  ) : null;

  if (variant === 'select') {
    return (
      <>
        {!skillLevelSimplified && <td className="px-3 py-2 align-top">{targetLevelField}</td>}
        <td className="px-3 py-2 align-top">
          <div className="flex items-center gap-2 flex-wrap">{dueDateField}{resultBadge}</div>
          {conflictNote}
        </td>
        <td className="px-3 py-2 align-top">{detailLink}</td>
      </>
    );
  }
  return (
    <>
      {!skillLevelSimplified && (
        <div><span className="text-xs text-text-sub">目標レベル</span>{targetLevelField}</div>
      )}
      <div>
        <span className="text-xs text-text-sub">目標達成時期</span>
        <div className="flex items-center gap-2 flex-wrap">{dueDateField}{resultBadge}</div>
      </div>
      {conflictNote}
      {detailLink && <div>{detailLink}</div>}
    </>
  );
}

/** §4-7 ルール3: 「+ スキルを追加」検索ポップオーバー。/masters/skills 全件をクライアント側で
 *  ラベル部分一致フィルタする簡易検索。 */
function AddSkillPopover({
  open, onClose, skillsMaster, rows, skillLevelSimplified, defaultLevelId, skillLevelsMaster, onAdd,
}: {
  open: boolean; onClose: () => void;
  skillsMaster: { skill_id: string; label: string; scale_id: string | null }[];
  rows: SkillMatrixRow[]; skillLevelSimplified: boolean; defaultLevelId: string | undefined;
  skillLevelsMaster: SkillMatrixMasterLevel[];
  onAdd: (skillId: string, levelId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [pendingSkillId, setPendingSkillId] = useState('');
  const [pendingLevelId, setPendingLevelId] = useState('');
  useEffect(() => { if (!open) { setSearch(''); setPendingSkillId(''); setPendingLevelId(''); } }, [open]);

  const existingIds = new Set(rows.map((r) => r.skillId));
  const candidates = skillsMaster.filter((s) => !existingIds.has(s.skill_id) && s.label.includes(search));
  const pendingScaleId = skillsMaster.find((s) => s.skill_id === pendingSkillId)?.scale_id ?? null;
  const levelsForPending = skillLevelsMaster.filter((l) => l.scale_id === pendingScaleId).sort((a, b) => a.sort_order - b.sort_order);

  return (
    <Modal open={open} title="スキルを追加" onClose={onClose} size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!pendingSkillId || (!skillLevelSimplified && !pendingLevelId)}
            onClick={() => onAdd(pendingSkillId, skillLevelSimplified ? (defaultLevelId ?? '') : pendingLevelId)}>
            追加
          </Button>
        </>
      )}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="add-skill-search">スキルを検索</label>
          <input id="add-skill-search" type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPendingSkillId(''); }}
            placeholder="スキル名で絞り込み" className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
        </div>
        <ul className="max-h-52 overflow-y-auto border border-surface-border rounded divide-y divide-surface-line">
          {candidates.map((s) => (
            <li key={s.skill_id}>
              <button type="button" onClick={() => { setPendingSkillId(s.skill_id); setPendingLevelId(''); }}
                className={`w-full text-left px-3 py-2 min-h-[44px] text-sm hover:bg-surface-sunken ${pendingSkillId === s.skill_id ? 'bg-surface-sunken font-semibold' : ''}`}>
                {s.label}
              </button>
            </li>
          ))}
          {candidates.length === 0 && <li className="px-3 py-2 text-sm text-text-sub">該当するスキルがありません</li>}
        </ul>
        {!skillLevelSimplified && pendingSkillId && (
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="add-skill-level">習熟度</label>
            <Select value={pendingLevelId || undefined} onValueChange={setPendingLevelId}>
              <SelectTrigger id="add-skill-level" className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
              <SelectContent>{levelsForPending.map((l) => <SelectItem key={l.skill_level_id} value={l.skill_level_id}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
    </Modal>
  );
}

const STATUS_LABELS: Record<DevelopmentPlanApi['status'], string> = {
  active: '進行中', pending_judgment: '判定待ち', achieved: '達成', not_achieved: '未達', cancelled: '取消',
};

/** §4-3 ルール4/§4-1「詳細」: 対象スキルの development_plans 全件 (重複含む) を一覧表示する。
 *  旧 DevelopmentPlanSection.tsx のリスト表示ロジックを1スキル分に絞り込んで流用。 */
function SkillPlanHistoryModal({
  skillId, skillLabel, plans, skillLevelsMaster, canManage, onClose, onChanged,
}: {
  employeeId: string; skillId: string; skillLabel: string; plans: DevelopmentPlanApi[];
  skillLevelsMaster: SkillMatrixMasterLevel[]; canManage: boolean; onClose: () => void; onChanged: () => void;
}) {
  const [cancelTarget, setCancelTarget] = useState<DevelopmentPlanApi | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DevelopmentPlanApi | null>(null);
  const levelLabel = (id: string) => skillLevelsMaster.find((l) => l.skill_level_id === id)?.label ?? id;

  const cancelMut = useMutation({
    mutationFn: (p: DevelopmentPlanApi) => apiFetch(`/development-plans/${p.plan_id}`, {
      method: 'PUT', body: JSON.stringify({ status: 'cancelled', row_version: p.row_version }),
    }),
    onSuccess: () => { onChanged(); setCancelTarget(null); toast.success('取消しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '取消に失敗しました'); setCancelTarget(null); },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/development-plans/${id}`, { method: 'DELETE' }),
    onSuccess: () => { onChanged(); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const sorted = [...plans].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

  return (
    <Modal open title={`${skillLabel} の育成計画`} onClose={onClose} size="sm">
      {sorted.length === 0 ? (
        <p className="text-sm text-text-sub">育成計画がまだ登録されていません。</p>
      ) : (
        <ul className="space-y-2">
          {sorted.map((p) => (
            <li key={p.plan_id} className="border border-surface-border rounded-lg p-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] px-1.5 py-0.5 rounded border border-surface-border bg-surface-sunken text-text-sub whitespace-nowrap">
                      {STATUS_LABELS[p.status]}
                    </span>
                  </div>
                  <p className="text-xs text-text-sub break-words">目標: {levelLabel(p.target_scale_level_id)}</p>
                  {(p.start_date || p.due_date) && (
                    <p className="text-[11px] text-text-sub">期間: {p.start_date ?? '未設定'} 〜 {p.due_date ?? '未設定'}</p>
                  )}
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    {(p.status === 'active' || p.status === 'pending_judgment') && (
                      <button type="button" aria-label="この計画を取消" onClick={() => setCancelTarget(p)}
                        className="inline-flex items-center justify-center gap-1 text-[11px] text-text-sub underline underline-offset-2 min-h-[44px] min-w-[44px] px-2">
                        <XCircle size={12} aria-hidden />取消
                      </button>
                    )}
                    <button type="button" aria-label="この計画を削除" onClick={() => setDeleteTarget(p)}
                      className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!cancelTarget} title="育成計画を取消しますか" onClose={() => setCancelTarget(null)}
        onConfirm={() => cancelTarget && cancelMut.mutate(cancelTarget)} confirmLabel="取消する" loading={cancelMut.isPending}
        message={cancelTarget && `「${skillLabel}」の育成計画を取消します。`}
      />
      <ConfirmDialog
        open={!!deleteTarget} title="育成計画を削除しますか" onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.plan_id)} confirmLabel="削除する" loading={deleteMut.isPending} destructive
        message={deleteTarget && `「${skillLabel}」の育成計画を削除します。この操作は取り消せません。`}
      />
    </Modal>
  );
}
